import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers, noString } from "./shared";
import { Patient } from "../patient.model";

interface PainFields {
  painRating: string;
  painScale: string;
  painLocation: string;
  painQuality: string;
  painQualityOther: string;
  otherFindings: string;

  // OB Only
  hasIntrapartumPain: boolean;
  hasAnalgesia: boolean;
  hasEpiduralPresent: boolean;
  nitrousOxide: boolean;
  opioids: boolean;
  analgesiaOther: string;

  // Non-pharmacologic Interventions
  isWalking: boolean;
  positionChange: string;
  hasBirthingBall: boolean;
  warmBath: string;
  nonPharmacologicOther: string;
}
export type PainFormGroup = FormGroup<ControlsOf<PainFields>>;

export const PainAssessmentHelpers: AssessmentHelpers<PainAssessment, Patient> = {
  createFormGroup: createPainFormGroup,
  toPDF: painAssessmentToPDF,
  formToObject: formToPainAssessment,
  initialState: {
    painRating: '',
    painScale: '',
    painLocation: '',
    painQuality: '',
    painQualityOther: '',
    otherFindings: '',

    hasIntrapartumPain: false,
    hasAnalgesia: false,
    hasEpiduralPresent: false,
    nitrousOxide: false,
    opioids: false,
    analgesiaOther: '',

    isWalking: false,
    positionChange: '',
    hasBirthingBall: false,
    warmBath: '',
    nonPharmacologicOther: '',
  },
};

export class PainAssessment {
  painRating: string;
  painScale: string;
  painLocation: string;
  painQuality: string;
  painQualityOther: string;
  otherFindings: string;

  // OB only
  hasIntrapartumPain: boolean;
  hasAnalgesia: boolean;
  hasEpiduralPresent: boolean;
  nitrousOxide: boolean;
  opioids: boolean;
  analgesiaOther: string;
  // Non-pharmacologic Interventions
  isWalking: boolean;
  positionChange: string;
  hasBirthingBall: boolean;
  warmBath: string;
  nonPharmacologicOther: string;

  constructor(data: Partial<PainFields>) {
    this.painRating = data.painRating ?? PainAssessmentHelpers.initialState['painRating'];
    this.painScale = data.painScale ?? PainAssessmentHelpers.initialState['painScale'];
    this.painLocation = data.painLocation ?? PainAssessmentHelpers.initialState['painLocation'];
    this.painQuality = data.painQuality ?? PainAssessmentHelpers.initialState['painQuality'];
    this.painQualityOther = data.painQualityOther ?? PainAssessmentHelpers.initialState['painQualityOther'];
    this.otherFindings = data.otherFindings ?? PainAssessmentHelpers.initialState['otherFindings'];
    // Only for OB:
    this.hasIntrapartumPain = data.hasIntrapartumPain ?? PainAssessmentHelpers.initialState['hasIntrapartumPain'];
    this.hasAnalgesia = data.hasAnalgesia ?? PainAssessmentHelpers.initialState['hasAnalgesia'];
    this.hasEpiduralPresent = data.hasEpiduralPresent ?? PainAssessmentHelpers.initialState['hasEpiduralPresent'];
    this.nitrousOxide = data.nitrousOxide ?? PainAssessmentHelpers.initialState['nitrousOxide'];
    this.opioids = data.opioids ?? PainAssessmentHelpers.initialState['opioids'];
    this.analgesiaOther = data.analgesiaOther ?? PainAssessmentHelpers.initialState['analgesiaOther'];
    this.isWalking = data.isWalking ?? PainAssessmentHelpers.initialState['isWalking'];
    this.positionChange = data.positionChange ?? PainAssessmentHelpers.initialState['positionChange'];
    this.hasBirthingBall = data.hasBirthingBall ?? PainAssessmentHelpers.initialState['hasBirthingBall'];
    this.warmBath = data.warmBath ?? PainAssessmentHelpers.initialState['warmBath'];
    this.nonPharmacologicOther = data.nonPharmacologicOther ?? PainAssessmentHelpers.initialState['nonPharmacologicOther'];
  }
}

function createPainFormGroup(painAssessment?: PainAssessment) {
  const data = painAssessment || new PainAssessment(PainAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<PainFields>>({
    painRating: new FormControl<string | null>(data.painRating),
    painScale: new FormControl<string | null>(data.painScale),
    painLocation: new FormControl<string | null>(data.painLocation),
    painQuality: new FormControl<string | null>(data.painQuality),
    painQualityOther: new FormControl<string | null>(data.painQualityOther),
    otherFindings: new FormControl<string | null>(data.otherFindings),

    // OB Only
    hasIntrapartumPain: new FormControl<boolean | null>(data.hasIntrapartumPain),
    hasAnalgesia: new FormControl<boolean | null>(data.hasAnalgesia),
    hasEpiduralPresent: new FormControl<boolean | null>(data.hasEpiduralPresent),
    nitrousOxide: new FormControl<boolean | null>(data.nitrousOxide),
    opioids: new FormControl<boolean | null>(data.opioids),
    analgesiaOther: new FormControl<string | null>(data.analgesiaOther),

    // Non-pharmacologic Interventions
    isWalking: new FormControl<boolean | null>(data.isWalking),
    positionChange: new FormControl<string | null>(data.positionChange),
    hasBirthingBall: new FormControl<boolean | null>(data.hasBirthingBall),
    warmBath: new FormControl<string | null>(data.warmBath),
    nonPharmacologicOther: new FormControl<string | null>(data.nonPharmacologicOther),
  });
}

function painAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: PainAssessment, patient: Patient): void {
  pdf.addSectionTitle("Pain Assessment");
  pdf.addTwoColumnText("Pain Rating(1-10)", assessment.painRating,
    "Pain Scale", assessment.painScale);
  pdf.addTwoColumnText("Pain Location", assessment.painLocation,
    "Pain Quality", assessment.painQuality);
  pdf.addOneColumnText("Other Findings", assessment.otherFindings);

  if (patient.isObstetric() && assessment.hasIntrapartumPain) {
    if (assessment.hasAnalgesia) {
      pdf.currY += 10;
      pdf.pdf.text("Analgesia", pdf.margin, pdf.currY);
      pdf.currY += 20;

      pdf.addTwoColumnText("Epidural Present", assessment.hasEpiduralPresent ? "Yes" : "No",
        "Nitrous Oxide", assessment.nitrousOxide ? "Yes" : "No");
      pdf.addTwoColumnText("Opioids", assessment.opioids ? "Yes" : "No",
        "Other", assessment.analgesiaOther);

      pdf.currY += 10;
      pdf.pdf.text("Non-Pharmacologic Interventions", pdf.margin, pdf.currY);
      pdf.currY += 20;

      pdf.addTwoColumnText("Is Walking?", assessment.isWalking ? "Yes" : "No",
        "Position Change", assessment.positionChange);
      pdf.addTwoColumnText("Birthing Ball?", assessment.hasBirthingBall ? "Yes" : "No",
        "Warm Bath?", assessment.warmBath);
      pdf.addOneColumnText("Other", assessment.nonPharmacologicOther);
    }
  }
  pdf.currY += pdf.newSection;
}

function formToPainAssessment(fg: PainFormGroup) {
  return new PainAssessment({
    painRating: fg.controls.painRating.value!,
    painScale: fg.controls.painScale.value!,
    painLocation: fg.controls.painLocation.value!,
    painQuality: fg.controls.painQuality.value!,
    painQualityOther: fg.controls.painQualityOther.value!,
    otherFindings: fg.controls.otherFindings.value!,
    hasIntrapartumPain: fg.controls.hasIntrapartumPain.value!,
    hasAnalgesia: fg.controls.hasAnalgesia.value!,
    hasEpiduralPresent: fg.controls.hasEpiduralPresent.value!,
    nitrousOxide: fg.controls.nitrousOxide.value!,
    opioids: fg.controls.opioids.value!,
    analgesiaOther: fg.controls.analgesiaOther.value!,
    isWalking: fg.controls.isWalking.value!,
    positionChange: fg.controls.positionChange.value!,
    hasBirthingBall: fg.controls.hasBirthingBall.value!,
    warmBath: fg.controls.warmBath.value!,
    nonPharmacologicOther: fg.controls.nonPharmacologicOther.value!
  });
}

